import Config from 'react-native-config';
import axios from 'axios';
import { MOBILE_PLATFORM_URL } from 'lib/deepLink/constants';
import { RouteLinkGenerator } from 'store/deepLink/utils';
import { Platform } from 'react-native';

const { TPS_BASE_URL } = Config;

export enum TpsSource {
  FHIR = 'fhir',
  VISIT_PAY = 'visitpay'
}

export type TpsAuthStatus = 'Unauthenticated' | 'Authenticated';

export interface TpsStatus {
  id: string;
  name: string;
  status: TpsAuthStatus;
  accessToken: string;
  expirationDate: string;
}

interface TPSParams {
  token: string;
  apiKey: string;
  redirect: string;
  source?: {
    id: TpsSource;
  };
  inAppBrowser?: any;
}

export interface TPSToken {
  accessToken: string;
  expirationDate: string;
}

const createTPSHeaders = ({ apiKey, token }: TPSParams) => ({
  apiKey,
  Authorization: `Bearer ${token}`
});

const getAuthUrl = async ({ token, apiKey, source, redirect }: TPSParams) => {
  const { id } = source || { id: '' };

  const url = `${TPS_BASE_URL}/auth`;

  return axios(url, {
    headers: createTPSHeaders({ apiKey, token }),
    params: {
      redirectUri: redirect,
      sourceId: id
    }
  }).then(res => res.data.authUrl);
};

const authenticateWithSource = async (params: TPSParams) => {
  const authUrl = await getAuthUrl(params);
  const { inAppBrowser, redirect } = params;

  if (inAppBrowser && (await inAppBrowser.isAvailable())) {
    await inAppBrowser.openAuth(authUrl, redirect, {
      forceCloseOnRedirection: false,
      browserPackage: '' // TODO (track & remove): Temporarily solves FireFox bug with latest InAppBrowser 3.5.1 - https://github.com/proyecto26/react-native-inappbrowser/issues/206#issuecomment-713219355
    });

    return true;
  }

  window.location = authUrl;
  return false;
};

export const checkAuthSources = ({ token, apiKey }: TPSParams) => {
  const url = `${TPS_BASE_URL}/client/source`;

  return axios
    .get<TpsStatus[]>(url, {
      headers: createTPSHeaders({ apiKey, token })
    })
    .then(res => res.data);
};

export const authenticateTPS = async (
  { token, apiKey, inAppBrowser }: TPSParams,
  isWeb?: boolean
) => {
  const sources = await checkAuthSources({ token, apiKey });

  const mobileAuthDeeplink = `${MOBILE_PLATFORM_URL}/${RouteLinkGenerator.authCallback().close}`;
  const encodedMobileLink =
    Platform.OS === 'ios' ? encodeURIComponent(mobileAuthDeeplink) : mobileAuthDeeplink;

  const redirect = isWeb ? window.location.origin : encodedMobileLink;

  for (const source of sources) {
    if (source.status.toLowerCase() !== 'authenticated') {
      // eslint-disable-next-line no-await-in-loop
      await authenticateWithSource({ token, apiKey, source, inAppBrowser, redirect });
    }
  }

  return checkAuthSources({ token, apiKey });
};

export const fetchTPSTokenForSource = async (sourceId: string, { token, apiKey }: TPSParams) => {
  const url = `${TPS_BASE_URL}/service/token`;

  return axios
    .get<TPSToken>(url, {
      headers: createTPSHeaders({ apiKey, token }),
      params: {
        sourceId,
        persist: true
      }
    })
    .then(result => result.data);
};

export const isAuthenticatedWithSource = async ({
  token,
  apiKey,
  source = { id: '' }
}: TPSParams) => {
  const authSources = await checkAuthSources({ token, apiKey });

  for (const authSource of authSources) {
    if (
      authSource.status.toLowerCase() !== 'authenticated' &&
      authSource.id.toLowerCase() === source.id
    ) {
      return false;
    }
  }
  return true;
};
